import { useEffect, useRef, useCallback } from "react";

export interface UseThrottledOptions<T extends (...args: any[]) => any> {
  callback: T;
  delay?: number;
  leading?: boolean;
}

export function useThrottle<T extends (...args: any[]) => any>({
  callback,
  delay = 300,
  leading = false,
}: UseThrottledOptions<T>) {
  const callbackRef = useRef(callback);
  const isLeadingCallRef = useRef(leading);
  const lastCallRef = useRef(0);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const throttledFunction = useCallback(
    (...args: Parameters<T>) => {
      const now = new Date().getTime();

      if (leading && isLeadingCallRef.current) {
        callback(...args);
        lastCallRef.current = now;
        isLeadingCallRef.current = false;
        return;
      }

      if (now - lastCallRef.current > delay) {
        callbackRef.current(...args);
        lastCallRef.current = now;
      }
    },
    [delay, leading, callback],
  );

  return throttledFunction;
}
